import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useCliente } from "../context/ClienteContext";
import { ModuleWorkspace } from "../components/workspace/ModuleWorkspace";
import { isSupervisor } from "../utils/auth";
import ModuleHeader from "../components/ui/ModuleHeader";
import BackupMonitorPanel from "./backups/BackupMonitorPanel";
import { getBackupLogHistory, getLatestBackupLogs } from "../api";
import BackupProfilesReview from "./backups/BackupProfilesReview";
import "./BackupsModule.css";

const dateFormatter = new Intl.DateTimeFormat("es-ES", {
  timeZone: "Atlantic/Canary",
  dateStyle: "short",
  timeStyle: "short",
});
const fmtDate = (value) => {
  if (!value) return "—";
  try {
    return dateFormatter.format(new Date(value));
  } catch {
    return String(value);
  }
};
const fmtSizeMB = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return "—";
  return number >= 1024
    ? `${(number / 1024).toFixed(number / 1024 >= 10 ? 0 : 1)} GB`
    : `${Math.round(number)} MB`;
};
const resultTone = (value) => {
  const text = String(value || "").toLowerCase();
  if (text.includes("exitoso")) return "ok";
  if (text.includes("fall") || text.includes("no puedo conectar")) return "bad";
  if (text.includes("abort")) return "warn";
  return "neutral";
};
const STATUS_META = {
  failing: { label: "Fallando", tone: "bad", priority: 0 },
  overdue: { label: "Atrasada", tone: "bad", priority: 1 },
  no_data: { label: "Sin datos", tone: "warn", priority: 2 },
  review: { label: "Revisar resultado", tone: "warn", priority: 3 },
  pending_review: {
    label: "Pendiente de configurar",
    tone: "neutral",
    priority: 4,
  },
  ok: { label: "Correcta", tone: "ok", priority: 5 },
  archived: { label: "Archivada", tone: "neutral", priority: 6 },
};
const stateMeta = (row) =>
  STATUS_META[row?.status] || {
    label: row?.resultado || "Sin clasificar",
    tone: resultTone(row?.resultado),
    priority: 3,
  };
const rowKey = (row) =>
  String(row?.backup_profile_id || `legacy:${row?.perfil || ""}`);


function GlobalBackupsOverview({ onOpen }) {
  const [rows, setRows] = useState([]); const [loading, setLoading] = useState(true); const [error, setError] = useState(""); const [attentionOnly, setAttentionOnly] = useState(false);
  const load = useCallback(async () => { setLoading(true); setError(""); try { const response=await getLatestBackupLogs(); setRows(Array.isArray(response.data)?response.data:[]); } catch(e) { setRows([]); setError(e?.response?.data?.error||e.message||"No se pudieron cargar las copias."); } finally { setLoading(false); } }, []);
  useEffect(() => { load(); }, [load]);
  const visible = useMemo(() => rows.filter((row) => !attentionOnly || stateMeta(row).priority <= 3), [rows, attentionOnly]);
  const attention = rows.filter((row) => stateMeta(row).priority <= 3).length;
  return <div className="bk-global"><ModuleHeader eyebrow="Operación" title="Copias" description="Gestión de tareas, ejecuciones e informes de copia." actions={<button className="button secondary" onClick={load} disabled={loading} type="button">{loading ? "Actualizando…" : "Actualizar"}</button>}><span className="equiposScope">Todos los clientes</span></ModuleHeader><section className="card bk-global-primary"><div><h2>Megaprint</h2><p>Selecciona un cliente en el contexto superior para gestionar sus tareas, ejecuciones e informes de copia.</p></div><span className="bk-pill neutral">Fuente principal</span></section><section className="card bk-global-card"><header className="bk-global-head"><div><h2>Histórico SyncBack</h2><p>{loading ? "Cargando perfiles…" : String(attention) + " perfiles requieren revisión durante la transición"}</p></div><button type="button" className={"pillBtn " + (attentionOnly ? "on" : "")} onClick={() => setAttentionOnly((value) => !value)}>Solo requieren atención</button></header>{error ? <div className="bk-error">{error}</div> : null}<div className="bk-global-list">{!loading && !error && !visible.length ? <div className="bk-muted">No hay perfiles con este filtro.</div> : visible.map((row) => { const meta=stateMeta(row); return <button className="bk-global-row" key={rowKey(row) + ":" + row.cliente_id} onClick={() => onOpen(row)} type="button"><div><strong>{row.perfil || "Perfil sin identificar"}</strong><span>{row.nombre_cliente || "Cliente sin identificar"} · Última: {fmtDate(row.inicio_iso || row.fecha_importacion)}</span></div><span className={"bk-pill " + meta.tone}>{meta.label}</span></button>; })}</div></section><section className="card bk-global-source-note"><strong>Megaprint</strong><span>Las tareas propias se gestionan al seleccionar un cliente, dentro de esta misma sección.</span></section></div>;
}

export default function BackupsModule({ initialSource = "legacy" }) {
  const { clienteInfo, clienteSeleccionadoId, seleccionarCliente } = useCliente();
  const clientId = clienteSeleccionadoId ?? clienteInfo?.id ?? null;
  const identity =
    localStorage.getItem("email") || localStorage.getItem("usuario") || "user";
  const storageKey = `workspace:${identity}:backups:${clientId || "none"}`;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeKey, setActiveKey] = useState(
    () => sessionStorage.getItem(`${storageKey}:profile`) || "",
  );
  const [query, setQuery] = useState(
    () => sessionStorage.getItem(`${storageKey}:query`) || "",
  );
  const [attentionOnly, setAttentionOnly] = useState(
    () => sessionStorage.getItem(`${storageKey}:attention`) === "1",
  );
  const [history, setHistory] = useState([]);
  const [historyVisible, setHistoryVisible] = useState(5);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [detailOpen, setDetailOpen] = useState(false);
  const [profilesReviewOpen, setProfilesReviewOpen] = useState(false);
  const [source, setSource] = useState(initialSource);
  useEffect(() => { setSource(initialSource); }, [initialSource]);
  const load = useCallback(async () => {
    if (!clientId) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await getLatestBackupLogs();
      const data = response.data;
      const filtered = (Array.isArray(data) ? data : [])
        .filter((row) => String(row.cliente_id) === String(clientId))
        .sort(
          (a, b) =>
            stateMeta(a).priority - stateMeta(b).priority ||
            String(a.perfil || "").localeCompare(String(b.perfil || "")),
        );
      setRows(filtered);
      setActiveKey((current) =>
        current && filtered.some((row) => rowKey(row) === current)
          ? current
          : rowKey(filtered[0]),
      );
    } catch (requestError) {
      setError(requestError.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [clientId]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    setActiveKey(sessionStorage.getItem(`${storageKey}:profile`) || "");
    setQuery(sessionStorage.getItem(`${storageKey}:query`) || "");
    setAttentionOnly(sessionStorage.getItem(`${storageKey}:attention`) === "1");
    setDetailOpen(false);
  }, [storageKey]);
  useEffect(() => {
    if (activeKey) sessionStorage.setItem(`${storageKey}:profile`, activeKey);
  }, [activeKey, storageKey]);
  useEffect(() => {
    sessionStorage.setItem(`${storageKey}:query`, query);
    sessionStorage.setItem(
      `${storageKey}:attention`,
      attentionOnly ? "1" : "0",
    );
  }, [storageKey, query, attentionOnly]);
  const selected = useMemo(
    () => rows.find((row) => rowKey(row) === activeKey) || rows[0] || null,
    [rows, activeKey],
  );
  const visibleRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter(
      (row) =>
        (!q ||
          `${row.perfil || ""} ${row.resultado || ""} ${stateMeta(row).label}`
            .toLowerCase()
            .includes(q)) &&
        (!attentionOnly || stateMeta(row).priority <= 3),
    );
  }, [rows, query, attentionOnly]);
  useEffect(() => {
    setHistoryVisible(5);
  }, [activeKey]);
  useEffect(() => {
    if (!selected || !clientId) {
      setHistory([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setHistoryLoading(true);
      setHistoryError("");
      try {
        const response = await getBackupLogHistory({
          cliente_id: String(clientId),
          perfil: selected.perfil || "",
        });
        const data = response.data;
        if (!cancelled) setHistory(Array.isArray(data) ? data : []);
      } catch (requestError) {
        if (!cancelled) {
          setHistory([]);
          setHistoryError(requestError.message);
        }
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selected, clientId]);
  if (!clientId || !clienteInfo) return <GlobalBackupsOverview onOpen={async (row) => { await seleccionarCliente(row.cliente_id); }} />;
  const select = (row) => {
    setActiveKey(rowKey(row));
    setDetailOpen(true);
  };
  return (
    <div className="bk-module">
      <ModuleHeader eyebrow="Operación" title="Copias" description="Consulta el estado y gestiona las fuentes de copia del cliente." clientDetails />
      <nav className="bk-source-tabs" aria-label="Fuente de copias"><button className={source === "megaprint" ? "is-active" : ""} onClick={() => setSource("megaprint")} type="button">Megaprint</button><button className={source === "legacy" ? "is-active" : ""} onClick={() => setSource("legacy")} type="button">SyncBack legado</button></nav>
      {source === "legacy" ? <>
      <ModuleWorkspace
        ariaLabel="Copias del cliente"
        detailOpen={detailOpen}
        onBackToList={() => setDetailOpen(false)}
        backLabel="Volver a perfiles"
        detailKey={activeKey}
        list={
          <aside className="card bk-work-list">
            <div className="bk-work-list-head">
              <div>
                <div className="bk-title">Perfiles de copia</div>
                <div className="bk-muted">
                  {visibleRows.length} de {rows.length}
                </div>
              </div>
              <button
                className="button small"
                onClick={load}
                disabled={loading}
              >
                {loading ? "…" : "Actualizar"}
              </button>
            </div>
            <input
              className="input"
              placeholder="Buscar perfil o estado…"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
            <button
              type="button"
              className={`pillBtn ${attentionOnly ? "on" : ""}`}
              onClick={() => setAttentionOnly((value) => !value)}
            >
              Solo requieren atención
            </button>
            {error ? <div className="bk-error">{error}</div> : null}
            <div className="bk-profile-nav">
              {loading ? (
                <div className="bk-muted">Cargando…</div>
              ) : visibleRows.length === 0 ? (
                <div className="bk-muted">
                  No hay perfiles con estos filtros.
                </div>
              ) : (
                visibleRows.map((row) => {
                  const meta = stateMeta(row);
                  return (
                    <button
                      type="button"
                      key={rowKey(row)}
                      className={`bk-profile-row ${rowKey(row) === rowKey(selected) ? "active" : ""}`}
                      onClick={() => select(row)}
                    >
                      <div className="bk-profile-row-head">
                        <strong>
                          {row.perfil || "Perfil sin identificar"}
                        </strong>
                        <span className={`bk-pill ${meta.tone}`}>
                          {meta.label}
                        </span>
                      </div>
                      <div className="bk-profile-row-sub">
                        Última:{" "}
                        {fmtDate(row.inicio_iso || row.fecha_importacion)}
                      </div>
                      <div className="bk-profile-row-result">
                        {row.resultado || "Sin resultado"}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </aside>
        }
        detail={
          <section className="bk-work-detail">
            {!selected ? (
              <div className="card">Selecciona un perfil.</div>
            ) : (
              <>
                <div className="card bk-detail-head">
                  <div>
                    <div className="bk-detail-title">
                      {selected.perfil || "Perfil sin identificar"}
                    </div>
                    <div className="bk-detail-sub">
                      Última ejecución:{" "}
                      {fmtDate(
                        selected.inicio_iso || selected.fecha_importacion,
                      )}
                    </div>
                  </div>
                  <div className="bk-detail-actions">
                    <span className={`bk-badge ${stateMeta(selected).tone}`}>
                      {stateMeta(selected).label}
                    </span>
                    {isSupervisor() ? (
                      <button
                        className="button secondary"
                        onClick={() => setProfilesReviewOpen(true)}
                      >
                        Configurar
                      </button>
                    ) : null}
                  </div>
                </div>
                <div className="card bk-detail-summary">
                  <div>
                    <span>Resultado</span>
                    <strong>{selected.resultado || "Sin resultado"}</strong>
                  </div>
                  <div>
                    <span>Inicio</span>
                    <strong>{selected.inicio || "—"}</strong>
                  </div>
                  <div>
                    <span>Fin</span>
                    <strong>{selected.fin_texto || "—"}</strong>
                  </div>
                  <div>
                    <span>Duración</span>
                    <strong>
                      {Number.isFinite(Number(selected.duracion_segundos))
                        ? `${selected.duracion_segundos}s`
                        : "—"}
                    </strong>
                  </div>
                  <div>
                    <span>Origen</span>
                    <strong>{fmtSizeMB(selected.espacio_origen_mb)}</strong>
                  </div>
                  <div>
                    <span>Destino</span>
                    <strong>{fmtSizeMB(selected.espacio_destino_mb)}</strong>
                  </div>
                </div>
                <div className="card bk-history">
                  <div className="bk-history-head">
                    <div>
                      <div className="bk-title">Historial</div>
                      <div className="bk-muted">
                        Mostrando {Math.min(historyVisible, history.length)} de{" "}
                        {history.length} ejecuciones
                      </div>
                    </div>
                    {history.length > 5 ? (
                      <div className="bk-history-more">
                        {historyVisible < history.length ? (
                          <button
                            className="button secondary"
                            type="button"
                            onClick={() =>
                              setHistoryVisible((value) =>
                                Math.min(value + 5, history.length),
                              )
                            }
                          >
                            Mostrar 5 más
                          </button>
                        ) : (
                          <button
                            className="button secondary"
                            type="button"
                            onClick={() => setHistoryVisible(5)}
                          >
                            Mostrar solo 5
                          </button>
                        )}
                      </div>
                    ) : null}
                  </div>
                  {historyLoading ? (
                    <div className="bk-muted">Cargando historial…</div>
                  ) : historyError ? (
                    <div className="bk-error">{historyError}</div>
                  ) : history.length === 0 ? (
                    <div className="bk-muted">Sin registros.</div>
                  ) : (
                    <div className="bk-tableWrap">
                      <table className="bk-table bk-history-table">
                        <thead>
                          <tr>
                            <th>Inicio</th>
                            <th>Fin</th>
                            <th>Resultado</th>
                            <th>Origen</th>
                            <th>Destino</th>
                          </tr>
                        </thead>
                        <tbody>
                          {history.slice(0, historyVisible).map((item) => (
                            <tr key={item.id}>
                              <td>{item.inicio || "—"}</td>
                              <td>{item.fin_texto || "—"}</td>
                              <td>
                                <span
                                  className={`bk-pill ${resultTone(item.resultado)}`}
                                >
                                  {item.resultado || "—"}
                                </span>
                              </td>
                              <td>{fmtSizeMB(item.espacio_origen_mb)}</td>
                              <td>{fmtSizeMB(item.espacio_destino_mb)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </section>
        }
      />
      {profilesReviewOpen ? (
        <BackupProfilesReview
          clientId={clientId}
          onClose={() => {
            setProfilesReviewOpen(false);
            load();
          }}
        />
      ) : null}
      </> : <BackupMonitorPanel clientId={clientId} />}
    </div>
  );
}
